import { useEffect, type RefObject } from 'react';

const FOCUSABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Elements atteignables au clavier, dans l'ordre du document. */
export function focusableIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('inert') && el.getAttribute('aria-hidden') !== 'true' && !el.closest('[inert]'),
  );
}

// Pile des pieges actifs : seul le plus recent (dialogue du dessus) agit.
const stack: HTMLElement[] = [];

/**
 * Piege le focus dans un dialogue maison (motion.div role="dialog") : Tab et
 * Maj+Tab bouclent dans le dialogue au lieu de partir vers la page derriere.
 * Un dialogue Radix ouvert par-dessus garde la main (son propre piege).
 * A la fermeture, le focus revient a l'element qui l'avait avant.
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, active = true): void {
  useEffect(() => {
    if (!active) return;
    const root = ref.current;
    if (!root) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    stack.push(root);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || stack[stack.length - 1] !== root) return;
      const current = document.activeElement as HTMLElement | null;
      // Focus dans un autre dialogue (Radix, porte ailleurs dans le DOM) : on le laisse faire.
      if (current && !root.contains(current) && current.closest('[role="dialog"],[role="alertdialog"]')) return;
      const items = focusableIn(root);
      if (items.length === 0) {
        e.preventDefault();
        root.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const outside = !current || !root.contains(current);
      if (e.shiftKey && (outside || current === first)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (outside || current === last)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const i = stack.lastIndexOf(root);
      if (i >= 0) stack.splice(i, 1);
      if (previous && previous.isConnected && !root.contains(previous)) previous.focus({ preventScroll: true });
    };
  }, [ref, active]);
}
